"use client";

// ── The problem set PDF on the contest page (`EX-PDF05`) ─────────────────────
// Two audiences, two routes, one press each (the server's defaults, so no
// option is sent and nothing here can drift from the founder's one press):
//
// - Wave 26 decision 8: once a public, listed round has ended, anyone signed
//   in downloads the set the page shows (`GET /v1/contests/:slug/problem-set/pdf`,
//   R4). The page payload's `problemSetPdf` says whether the button exists;
//   signed out it leads to sign-in and back, because every copy carries its
//   reader's name. An issued contest login never sees it (the route is off its
//   allowlist).
// - Decision 7, D3: the round's staff read the statements before the start, so
//   they print them from here too (`GET /v1/contest-platform/contests/:id/problem-set/pdf`,
//   R1). A tester has no row on the Studio's "Rounds you author" (that read
//   lists authors only), so this page was the one place they had none.

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Download, LogIn, Printer } from "lucide-react";

import { cn } from "@/lib/utils";
import { signInHref } from "@/lib/post-login-redirect";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/providers/auth-provider";
import type { PdfAction } from "@/features/problem-set-pdf/pdf-delivery";
import { PdfRunStatus } from "@/features/problem-set-pdf/PdfControls";
import { publicProblemSetPdfPath } from "../api";
import { useContestPdf } from "../hooks";
import { fallbackPdfName } from "../lib/problem-set-pdf";
import { staffReads } from "../lib/rehearsal";
import type { PublicContestDetail } from "../types";
import { ProblemSetPdfActions } from "./ProblemSetPdfActions";

type PdfContest = Pick<PublicContestDetail, "phase" | "visibility" | "problemSetPdf" | "problems" | "viewer">;
type AccountKind = "full" | "contest_temp" | undefined;

/** What the page offers this reader of a finished round: nothing, a sign-in, or the PDF. */
export type PublicPdfOffer = "none" | "signIn" | "download";

function hasEnded(contest: Pick<PdfContest, "phase">): boolean {
  return contest.phase === "ended" || contest.phase === "unfrozen";
}

/**
 * Decision 8, read off the server's own flag (`problemSetPdf`: public, listed,
 * ended, with a problem). The phase and the visibility are asked again so a
 * page left open across the end, or an older payload, never shows the button
 * early; an issued login is refused by the server, so it gets no button.
 */
export function publicPdfOffer(contest: PdfContest, accountKind: AccountKind): PublicPdfOffer {
  if (contest.problemSetPdf !== true || contest.visibility !== "public" || !hasEnded(contest)) return "none";
  if (!contest.viewer.isAuthenticated) return "signIn";
  if (accountKind === "contest_temp") return "none";
  return "download";
}

/**
 * Decision 7: whoever holds `staff_read` on R1 (`assertContestScope`): the
 * moderating side (`canModerate`: the owner, the moderators, the lead authors,
 * the funding organization's admins, an operator) and every staff list but
 * the volunteers' (`staffReads`). Never a contestant or a volunteer. Offered
 * whenever the decision 8 button is not, so a private round's staff keep it
 * after the end too, and only while the round has a problem to print.
 */
export function staffPdfOffer(contest: PdfContest, accountKind: AccountKind): boolean {
  if (!contest.viewer.isAuthenticated || accountKind === "contest_temp") return false;
  if ((contest.problems?.length ?? 0) === 0) return false;
  if (!contest.viewer.canModerate && !staffReads(contest.viewer.staffRole)) return false;
  return publicPdfOffer(contest, accountKind) === "none";
}

function loginHref(locale: string, slug: string): string {
  return signInHref(locale, `/${locale}/contests/${slug}`);
}

/** The R4 run, one press, the server's defaults. */
function usePublicPdf(contest: Pick<PublicContestDetail, "_id" | "slug">) {
  const t = useTranslations("contest.problemSetPdf");
  const pdf = useContestPdf(contest._id);
  const start = (action: PdfAction) =>
    void pdf.start({
      path: publicProblemSetPdfPath(contest.slug),
      fallbackFilename: fallbackPdfName(contest.slug),
      action,
      waiting: t("printWaiting"),
    });
  return { run: pdf.run, start, making: pdf.run.status === "making" };
}

/** Decision 8 on the Problems tab: Print and Problem set PDF beside a line that says whose name is on it. */
export function ProblemSetPdfBar({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const { user } = useAuth();
  const offer = publicPdfOffer(contest, user?.accountKind);
  if (offer === "none") return null;
  return <ProblemSetPdfBarOffer contest={contest} locale={locale} offer={offer} />;
}

function ProblemSetPdfBarOffer({
  contest,
  locale,
  offer,
}: {
  contest: PublicContestDetail;
  locale: string;
  offer: Exclude<PublicPdfOffer, "none">;
}) {
  const t = useTranslations("contest.problemSetPdf.page");
  const pdf = usePublicPdf(contest);

  return (
    <section
      aria-label={t("heading")}
      className="rounded-xl border border-neutral-200 bg-white px-4 py-3.5 sm:px-[18px]"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900">{t("heading")}</p>
          <p className="mt-0.5 text-[13px] leading-relaxed text-neutral-500">
            {offer === "signIn" ? t("signInHint") : t("lead")}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {offer === "signIn" ? (
            <Button asChild size="sm" className="h-11 px-3.5 sm:h-9">
              <Link href={loginHref(locale, contest.slug)}>
                <LogIn aria-hidden className="h-3.5 w-3.5" />
                {t("download")}
              </Link>
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 px-3.5 sm:h-9"
                aria-busy={pdf.run.status === "making" && pdf.run.action === "print"}
                disabled={pdf.making}
                onClick={() => pdf.start("print")}
              >
                <Printer aria-hidden className="h-3.5 w-3.5" />
                {t("print")}
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-11 px-3.5 sm:h-9"
                aria-busy={pdf.run.status === "making" && pdf.run.action === "download"}
                disabled={pdf.making}
                onClick={() => pdf.start("download")}
              >
                <Download aria-hidden className="h-3.5 w-3.5" />
                {t("download")}
              </Button>
            </>
          )}
        </div>
      </div>
      <PdfRunStatus run={pdf.run} className="mt-2.5" />
    </section>
  );
}

/** Decision 8 in the ended door, under "See the standings", on its dark card. */
export function ProblemSetPdfDoor({ contest, locale, buttonClass }: { contest: PublicContestDetail; locale: string; buttonClass: string }) {
  const { user } = useAuth();
  const offer = publicPdfOffer(contest, user?.accountKind);
  if (offer === "none") return null;
  return <ProblemSetPdfDoorOffer contest={contest} locale={locale} offer={offer} buttonClass={buttonClass} />;
}

function ProblemSetPdfDoorOffer({
  contest,
  locale,
  offer,
  buttonClass,
}: {
  contest: PublicContestDetail;
  locale: string;
  offer: Exclude<PublicPdfOffer, "none">;
  buttonClass: string;
}) {
  const t = useTranslations("contest.problemSetPdf.page");
  const pdf = usePublicPdf(contest);

  if (offer === "signIn") {
    return (
      <div className="space-y-2">
        <Button asChild variant="outlineDark" className={buttonClass}>
          <Link href={loginHref(locale, contest.slug)}>
            <Download aria-hidden className="h-4 w-4" />
            {t("doorDownload")}
          </Link>
        </Button>
        <p className="text-xs leading-relaxed text-neutral-400">{t("signInHint")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2.5">
        <Button
          type="button"
          variant="outlineDark"
          className={buttonClass}
          aria-busy={pdf.run.status === "making" && pdf.run.action === "download"}
          disabled={pdf.making}
          onClick={() => pdf.start("download")}
        >
          <Download aria-hidden className="h-4 w-4" />
          {t("doorDownload")}
        </Button>
        <Button
          type="button"
          variant="outlineDark"
          className={cn(buttonClass, "w-auto px-4")}
          aria-busy={pdf.run.status === "making" && pdf.run.action === "print"}
          disabled={pdf.making}
          onClick={() => pdf.start("print")}
        >
          <Printer aria-hidden className="h-4 w-4" />
          {t("print")}
        </Button>
      </div>
      <PdfRunStatus run={pdf.run} tone="dark" />
    </div>
  );
}

/**
 * Decision 7 on the Problems tab, beside the rehearsal line: the round's staff
 * print the set they are reading, before the start and while it runs, with a
 * small "For staff" label so nobody mistakes it for the public button.
 */
export function StaffProblemSetPdf({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.problemSetPdf.page");
  const { user } = useAuth();
  if (!staffPdfOffer(contest, user?.accountKind)) return null;
  const beforeStart = contest.phase === "draft" || contest.phase === "scheduled";
  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;

  return (
    <section
      aria-label={t("staffTitle")}
      data-testid="staff-problem-set-pdf"
      className="rounded-xl border border-neutral-200 bg-white px-4 py-3.5 sm:px-[18px]"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-600">
              {t("staffLabel")}
            </span>
            <span className="text-sm font-semibold text-neutral-900">{t("staffTitle")}</span>
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-neutral-500">
            {t(beforeStart ? "staffBefore" : "staffLive")}
          </p>
        </div>
        <div className="shrink-0 sm:max-w-[320px]">
          <ProblemSetPdfActions contestId={contest._id} slug={contest.slug} title={title} />
        </div>
      </div>
    </section>
  );
}
